import { queryOptions } from "@tanstack/react-query";
import { ServedVersionSchema, type ServedVersion } from "@nexus/contracts";
import {
  HEALTH_POLL_INTERVAL,
  HEALTH_STALE_TIME_MS,
  RETRY_COUNT_DEFAULT,
} from "@/lib/query-constants";

/** Emitted into the bundle root at build time — see `frontend/web/vite.config.ts`. */
const SERVED_VERSION_PATH = "/version.json";

/**
 * Which bundle the web tier is handing out right now.
 *
 * Deliberately same-origin rather than the API: `api` and `app` are separate
 * Deployments that roll on their own schedules, so the API's `GIT_SHA` says
 * nothing about whether reloading would get this client newer code (AD-2221).
 */
async function fetchServedVersion(): Promise<ServedVersion> {
  const res = await fetch(SERVED_VERSION_PATH, { cache: "no-store" });
  if (!res.ok) throw new Error(`version.json returned ${res.status}`);
  const json: unknown = await res.json();
  const parsed = ServedVersionSchema.safeParse(json);
  if (!parsed.success) throw new Error("invalid version.json response");
  return parsed.data;
}

export const servedVersionKeys = {
  all: ["served-version"] as const,
} as const;

/**
 * Polls on the health cadence so a web-tier rollout is noticed within the same
 * window as an API one.
 */
export function servedVersionQueryOptions() {
  return queryOptions({
    queryKey: servedVersionKeys.all,
    queryFn: fetchServedVersion,
    refetchInterval: HEALTH_POLL_INTERVAL,
    // Matches the health poll: React Query skips interval fetches while the tab
    // is blurred, so without this a backgrounded tab never learns of a rollout.
    refetchOnWindowFocus: true,
    staleTime: HEALTH_STALE_TIME_MS,
    retry: RETRY_COUNT_DEFAULT,
    meta: { skipGlobalError: true },
  });
}
